import React, { useEffect, useState } from 'react';
import { AlarmClock, RefreshCw, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import apiService from '../services/api';
import logger from '../utils/logger';

interface ExpirationItem {
  id: number;
  clientName: string;
  clientInitials: string;
  procedure: string;
  lastDate: string;
  daysUntil: number;
}

interface ExpirationDashboard {
  id: number;
  generated_for_date: string;
  summary: string;
  items_count: number;
  items?: ExpirationItem[];
  model: string | null;
  created_at: string;
}

function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const STORAGE_KEY = 'na_expirations_seen_date';

const ExpirationsWidget: React.FC = () => {
  const { t } = useTranslation();
  const [dashboard, setDashboard] = useState<ExpirationDashboard | null>(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [seen, setSeen] = useState<boolean>(() => localStorage.getItem(STORAGE_KEY) === todayKey());

  const load = async () => {
    setLoading(true);
    try {
      const resp: any = await apiService.get('/api/expirations/dashboard');
      if (resp.success) setDashboard(resp.dashboard || null);
    } catch (err: any) {
      const status = err?.response?.status || err?.status;
      if (status !== 403) logger.error('Failed to load expirations dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const resp: any = await apiService.post('/api/expirations/run', { autoSend: false });
      if (resp.success) {
        setDashboard(resp.dashboard || dashboard);
        localStorage.removeItem(STORAGE_KEY);
        setSeen(false);
      }
    } catch (err) {
      logger.error('Failed to run expirations:', err);
    } finally {
      setRunning(false);
    }
  };

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, todayKey());
    setSeen(true);
  };

  if (loading || !dashboard) return null;
  if (seen) return null;
  if (dashboard.items_count === 0) return null;
  if (dashboard.generated_for_date !== todayKey()) return null;

  return (
    <div className="widget-v2">
      <div className="widget-v2-hd">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
          <div className="widget-v2-icon" style={{ background: 'color-mix(in srgb, var(--warning, #d97706) 14%, transparent)', color: 'var(--warning, #d97706)' }}>
            <AlarmClock size={14} />
          </div>
          <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0, color: 'var(--ink)' }}>
            {t('expirations.widget.title')}
          </h3>
          <span className="widget-v2-meta">
            {t('expirations.widget.count', { count: dashboard.items_count })}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <button onClick={runNow} disabled={running}
            style={{ background: 'none', border: 'none', padding: 6, cursor: 'pointer', color: 'var(--ink-muted)', borderRadius: 'var(--radius)', opacity: running ? 0.5 : 1 }}>
            <RefreshCw size={14} className={running ? 'animate-spin' : ''} />
          </button>
          <button onClick={dismiss}
            style={{ background: 'none', border: 'none', padding: 6, cursor: 'pointer', color: 'var(--ink-muted)', borderRadius: 'var(--radius)' }}>
            <X size={14} />
          </button>
        </div>
      </div>

      {dashboard.items && dashboard.items.length > 0 ? (
        <div className="exp-list">
          {dashboard.items.map(item => (
            <div key={item.id} className="exp-row">
              <div className="team-row-avatar" style={{ width: 32, height: 32, fontSize: 11 }}>
                {item.clientInitials}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 550, color: 'var(--ink)' }}>{item.clientName}</div>
                <div style={{ fontSize: 12, color: 'var(--ink-faint)' }}>
                  {item.procedure} · {item.lastDate}
                </div>
              </div>
              <span className={`exp-pill ${item.daysUntil < 0 ? 'over' : 'soon'}`}>
                {item.daysUntil < 0
                  ? t('expirations.widget.overdue', { days: Math.abs(item.daysUntil) })
                  : t('expirations.widget.inDays', { days: item.daysUntil })}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <pre style={{
          whiteSpace: 'pre-wrap', fontSize: 13, fontFamily: 'inherit',
          lineHeight: 1.6, color: 'var(--ink)', margin: 0,
          padding: '0 16px 14px',
        }}>
          {dashboard.summary}
        </pre>
      )}
    </div>
  );
};

export default ExpirationsWidget;
